import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import API from "../lib/api";
import { Users, Building2, FileText, TrendingUp, Moon, Sun, LogOut, Shield } from "lucide-react";

const PLAN_COLORS = {
  trial:      { bg: "rgba(100,116,139,0.12)", text: "#64748b", border: "rgba(100,116,139,0.25)" },
  starter:    { bg: "rgba(59,130,246,0.12)",  text: "#2563eb", border: "rgba(59,130,246,0.25)" },
  growth:     { bg: "rgba(5,150,105,0.12)",  text: "#059669", border: "rgba(5,150,105,0.25)" },
  enterprise: { bg: "rgba(139,92,246,0.12)",  text: "#7c3aed", border: "rgba(139,92,246,0.25)" },
};

const MERIT_BUCKETS = ["0–40", "40–60", "60–80", "80–100"];

function PlanBadge({ plan }) {
  const s = PLAN_COLORS[plan] || PLAN_COLORS.trial;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: s.bg, color: s.text, border: `1px solid ${s.border}`, textTransform: "capitalize" }}>
      {plan || "trial"}
    </span>
  );
}

export default function AdminPortal() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [analytics, setAnalytics] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [planLoading, setPlanLoading] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (user === undefined) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    Promise.all([
      API.get("/api/admin/analytics"),
      API.get("/api/admin/tenants"),
    ]).then(([a, t]) => {
      setAnalytics(a.data);
      setTenants(t.data);
    }).catch(console.error).finally(() => setLoadingData(false));
  }, [user, navigate]);

  const handlePlanChange = async (tenantId, newPlan) => {
    setPlanLoading(tenantId);
    try {
      await API.patch(`/api/admin/tenants/${tenantId}/plan`, { plan: newPlan });
      setTenants(prev => prev.map(t => t.id === tenantId ? { ...t, plan: newPlan } : t));
    } catch (e) {
      console.error(e);
    } finally {
      setPlanLoading(null);
    }
  };

  const pageBg = isDark ? "#0a0a0f" : "#f8f9fc";
  const headerBg = isDark ? "rgba(10,10,15,0.92)" : "rgba(255,255,255,0.95)";
  const textPrimary = isDark ? "#ffffff" : "#0a0a0f";
  const textSecondary = isDark ? "rgba(255,255,255,0.55)" : "#64748b";
  const textMuted = isDark ? "rgba(255,255,255,0.3)" : "#94a3b8";
  const cardStyle = {
    background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.07)",
    boxShadow: isDark ? "none" : "0 1px 8px rgba(0,0,0,0.05)",
    borderRadius: 12,
  };
  const tableBorder = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";

  const meritDist = analytics?.merit_distribution || {};
  const meritTotal = Object.values(meritDist).reduce((a, v) => a + v, 0) || 1;
  const meritKeys = ["0_40", "40_60", "60_80", "80_100"];
  const meritPcts = meritKeys.map(k => Math.round(((meritDist[k] || 0) / meritTotal) * 100));

  const statsCards = [
    { icon: <Users size={18} />, label: "Total Students", value: analytics?.total_students ?? "—", color: "#2563eb" },
    { icon: <Building2 size={18} />, label: "Recruiters", value: analytics?.total_recruiters ?? "—", color: "#7c3aed" },
    { icon: <FileText size={18} />, label: "Applications", value: analytics?.total_applications ?? "—", color: "#059669" },
    { icon: <TrendingUp size={18} />, label: "Placement Rate", value: analytics?.placement_rate != null ? `${analytics.placement_rate}%` : "—", color: "#059669" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: pageBg }} data-testid="admin-portal">
      {/* Header */}
      <header style={{ background: headerBg, backdropFilter: "blur(20px)", borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`, position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #1a1f5e, #2d3494)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Shield size={16} color="#fff" />
            </div>
            <div>
              <span style={{ fontWeight: 700, color: textPrimary, fontFamily: "'Plus Jakarta Sans', sans-serif", display: "block", lineHeight: 1.2 }}>Admin Portal</span>
              <span style={{ fontSize: 11, color: textMuted }}>WonStep Career</span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={e => toggleTheme(e)} aria-label="Toggle theme" style={{ width: 30, height: 30, borderRadius: "50%", background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: textSecondary }}>
              {isDark ? <Sun size={13} /> : <Moon size={13} />}
            </button>
            <button onClick={() => { logout(); window.location.replace("/"); }} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, padding: "6px 12px", borderRadius: 8, background: "transparent", border: "none", cursor: "pointer", color: textMuted }}>
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>
        {loadingData ? (
          <div style={{ display: "flex", justifyContent: "center", paddingTop: 80 }}>
            <div style={{ width: 32, height: 32, border: "3px solid #1a1f5e", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
          </div>
        ) : (
          <>
            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 28 }} data-testid="analytics-stats">
              {statsCards.map((s, i) => (
                <div key={i} style={{ ...cardStyle, padding: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, color: s.color }}>{s.icon}<span style={{ fontSize: 12, color: textSecondary }}>{s.label}</span></div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: textPrimary, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Merit Distribution */}
            <div style={{ ...cardStyle, padding: 24, marginBottom: 28 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: textPrimary, fontFamily: "'Plus Jakarta Sans', sans-serif", marginBottom: 16 }}>Merit Score Distribution</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {MERIT_BUCKETS.map((label, i) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 12, color: textSecondary, width: 56, flexShrink: 0 }}>{label}</span>
                    <div style={{ flex: 1, height: 10, borderRadius: 999, background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${meritPcts[i]}%`, background: "#059669", borderRadius: 999, transition: "width 0.5s ease" }} />
                    </div>
                    <span style={{ fontSize: 12, color: textMuted, width: 36, textAlign: "right" }}>{meritPcts[i]}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tenants Table */}
            <div style={{ ...cardStyle, overflow: "hidden" }}>
              <div style={{ padding: "18px 24px", borderBottom: `1px solid ${tableBorder}` }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: textPrimary, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Tenants ({tenants.length})</h2>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }} data-testid="tenants-table">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${tableBorder}` }}>
                      {["Company", "Plan", "Screens", "Jobs", "Override Plan"].map(h => (
                        <th key={h} style={{ padding: "10px 20px", textAlign: "left", color: textMuted, fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.length === 0 ? (
                      <tr><td colSpan={5} style={{ padding: "40px 20px", textAlign: "center", color: textMuted }}>No tenants found.</td></tr>
                    ) : tenants.map(tenant => (
                      <tr key={tenant.id} style={{ borderBottom: `1px solid ${tableBorder}` }}>
                        <td style={{ padding: "12px 20px", color: textPrimary, fontWeight: 500 }}>{tenant.company_name || tenant.name || "—"}</td>
                        <td style={{ padding: "12px 20px" }}><PlanBadge plan={tenant.plan} /></td>
                        <td style={{ padding: "12px 20px", color: textSecondary }}>{tenant.screens_used ?? 0} / {tenant.screens_limit ?? "—"}</td>
                        <td style={{ padding: "12px 20px", color: textSecondary }}>{tenant.jobs_active ?? 0} / {tenant.jobs_limit ?? "—"}</td>
                        <td style={{ padding: "12px 20px" }}>
                          <select
                            value={tenant.plan || "trial"}
                            onChange={e => handlePlanChange(tenant.id, e.target.value)}
                            disabled={planLoading === tenant.id}
                            style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, border: `1px solid ${tableBorder}`, background: isDark ? "rgba(255,255,255,0.07)" : "#f8f9fc", color: textPrimary, cursor: "pointer", outline: "none" }}
                          >
                            {["trial", "starter", "growth", "enterprise"].map(p => (
                              <option key={p} value={p} style={{ textTransform: "capitalize" }}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
